import {
  createFileRoute, redirect,
} from '@tanstack/react-router';
import AdditionalThirdPartiesWithToken from '../../../../components/pages/additional-third-parties-with-token';
import {
  isAdditionalThirdPartiesNotFound,
  isAdditionalThirdPartiesUnauthorized,
  isGoneError,
  isNotFoundError,
  isUnauthorizedError,
  NotFoundErrorCode,
} from '../../../../utils/errors';
import { getSettingsByUrlSlug } from '../../../../api/settings/get-by-url-slug';
import { getAdditionalThirdPartiesData } from '../../../../api/submissions/get-additional-third-parties';
import { AdditionalThirdPartiesLoaderResponse } from '../additional-third-parties/$type';
import { ThirdPartyType } from '../../../../types';

export interface AdditionalThirdPartiesSearchParams {
  token?: string;
}

interface AdditionalThirdPartiesWithTokenLoaderParams {
  params: {
    accountSlug: string;
    submissionId: string;
    token: string;
  };
}

async function additionalThirdPartiesLoader ({
  params: {
    accountSlug,
    submissionId,
    token: routeToken,
  },
}: AdditionalThirdPartiesWithTokenLoaderParams): Promise<AdditionalThirdPartiesLoaderResponse> {
  try {
    const additionalThirdPartiesData = await getAdditionalThirdPartiesData(accountSlug, submissionId, routeToken, ThirdPartyType.PERSONAL_GUARANTOR);

    // Remove token from URL after successful verification to clean up browser history
    // Using `replace: true` ensures the token URL doesn't remain in browser history
    if (routeToken) {
      // Redirect to clean URL without token
      throw redirect({
        to: `/${accountSlug}/${submissionId}/additional-third-parties/${ThirdPartyType.PERSONAL_GUARANTOR}`,
        replace: true,
      });
    }

    return {
      settings: additionalThirdPartiesData.settings,
      type: ThirdPartyType.PERSONAL_GUARANTOR,
    };
  } catch (error) {
    if (isNotFoundError(error) && error.code === NotFoundErrorCode.SettingsNotFound) {
      throw redirect({ to: '/error' });
    }

    if (isAdditionalThirdPartiesNotFound(error) ||
      isAdditionalThirdPartiesUnauthorized(error) ||
      isUnauthorizedError(error) ||
      isGoneError(error) ||
      (isNotFoundError(error) && error.code === NotFoundErrorCode.SubmissionNotFound)) {
      if (!isUnauthorizedError(error) && !isAdditionalThirdPartiesUnauthorized(error)) {
        window.DD_RUM?.addError(error, {
          source: 'additional-personal-guarantors-loader',
          accountSlug,
          submissionId,
        });
      }
      const settings = await getSettingsByUrlSlug(accountSlug);
      return {
        settings,
        error,
        type: ThirdPartyType.PERSONAL_GUARANTOR,
      };
    }

    window.DD_RUM?.addError(error, {
      source: 'additional-personal-guarantors-loader-unhandled',
      accountSlug,
      submissionId,
    });
    throw error;
  }
}

/**
 * Returns route configuration options for additional third parties routes
 * Can be spread directly in the route configuration
 */
function additionalThirdPartiesRouteLoader () {
  return {
    // Load data using the validated params
    loader: async ({ params }: AdditionalThirdPartiesWithTokenLoaderParams) =>
      additionalThirdPartiesLoader({ params }),
  };
}

export const Route = createFileRoute('/$accountSlug/$submissionId/additional-personal-guarantors-token/$token')({
  ...additionalThirdPartiesRouteLoader(),
  component: AdditionalThirdPartiesWithToken,
});
